import { useState } from 'react';
import { submitFeedback } from '@/api/feedback';
import { getSessionId } from '@/api/analytics';
import { showToast } from './Toasts';

export function FeedbackForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      showToast('Please select a rating', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitFeedback({
        session_id: getSessionId(),
        rating,
        comment: comment || undefined,
      });
      showToast('Thank you for your feedback!', 'success');
      setIsOpen(false);
      setRating(0);
      setComment('');
    } catch {
      showToast('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-bss-blue text-white shadow-lg shadow-blue-600/30 transition-all hover:scale-110 hover:shadow-blue-600/40 active:scale-95"
        aria-label="Give feedback"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm p-4 sm:items-center sm:justify-center animate-fade-in"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="w-full max-w-sm rounded-3xl glass-modal p-6 animate-modal-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">How was your experience?</h3>
          <button
            onClick={() => setIsOpen(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-5 flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className={`text-3xl transition-all duration-100 hover:scale-125 active:scale-110 ${
                  star <= (hoverRating || rating) ? 'text-amber-400' : 'text-slate-200'
                }`}
              >
                ★
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us what you think... (optional)"
            className="mb-4 w-full rounded-xl border border-slate-200 bg-white/80 p-3 text-sm text-slate-900 placeholder-slate-400 shadow-inner-glow focus:border-bss-blue focus:ring-2 focus:ring-bss-blue/20 focus:outline-none transition-all resize-none"
            rows={3}
            maxLength={500}
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-xl bg-bss-blue py-3 font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-bss-blue-dark hover:-translate-y-0.5 hover:shadow-blue-600/35 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      </div>
    </div>
  );
}
